import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLeague } from "@/lib/league-context";
import { ActivityFeedCard } from "@/components/ActivityFeedCard";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import {
  brewGoblinCommentary,
  getPendingGoblinCounts,
} from "@/lib/activity-feed.functions";
import { toast } from "sonner";
import { GoblinFeedViewToggle } from "@/components/GoblinFeedViewToggle";

export const Route = createFileRoute("/_app/$league/activity")({
  component: ActivityArchivePage,
  head: () => ({
    meta: [
      { title: "The Goblin's Take — Bracket Goblin" },
      {
        name: "description",
        content:
          "One bracket goblin, zero filter, all takes. Every match result, busted bracket, and league moment with the Goblin's commentary.",
      },
    ],
  }),
});

function ActivityArchivePage() {
  const { league: leagueSlug } = Route.useParams();
  const leagueCtx = useLeague();

  // Treat "post-lock" the same way the leaderboard does — once either
  // group or bracket lock has passed, picks are public and events flow.
  const now = Date.now();
  const groupLocked =
    !!leagueCtx?.groupStageLockAt &&
    new Date(leagueCtx.groupStageLockAt).getTime() <= now;
  const bracketLocked =
    !!leagueCtx?.bracketLockAt &&
    new Date(leagueCtx.bracketLockAt).getTime() <= now;
  const preLock = !groupLocked && !bracketLocked;

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <div className="mb-6">
        <Link
          to="/$league"
          params={{ league: leagueSlug }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to leaderboard
        </Link>
        <div className="mt-3 flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
              The Goblin's Take
            </h1>
            <p className="mt-1 text-sm text-muted-foreground italic">
              One bracket goblin. Zero filter. All takes. Sometimes the goblin goblins.
            </p>
          </div>
          {leagueCtx?.id && <BrewGoblinButton leagueId={leagueCtx.id} />}
        </div>
        <div className="mt-3 flex justify-end">
          <GoblinFeedViewToggle source="activity" />
        </div>
      </div>
      {leagueCtx?.id && (
        <ActivityFeedCard
          leagueId={leagueCtx.id}
          leagueSlug={leagueSlug}
          preLock={preLock}
          showViewToggle={false}
        />
      )}
    </main>
  );
}

// Admin-only: trigger the LLM batch to generate Goblin one-liners for any
// pending events. Manual trigger keeps cost/latency in our control for MVP.
function BrewGoblinButton({ leagueId }: { leagueId: string }) {
  const { isAdmin } = useAuth();
  const brew = useAuthedServerFn(brewGoblinCommentary);
  const getCounts = useAuthedServerFn(getPendingGoblinCounts);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<number | null>(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await getCounts();
      const row = res.byLeague.find((l) => l.leagueId === leagueId);
      setPending(row?.count ?? 0);
    } catch (e) {
      console.error("[BrewGoblinButton] getPendingGoblinCounts failed", e);
      setPending(-1);
    }
  }, [getCounts, leagueId]);

  useEffect(() => {
    if (!isAdmin) return;
    void refreshCount();
  }, [isAdmin, refreshCount]);

  if (!isAdmin) return null;

  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const res = await brew({ data: { leagueId, limit: 25 } });
          if (res.attempted === 0) {
            toast.success("No pending events — Goblin is caught up.");
            setPending(0);
          } else {
            toast.success(
              `Goblin brewed ${res.succeeded}/${res.attempted}${res.failed ? ` (${res.failed} failed)` : ""}.`,
            );
            await refreshCount();
            // Soft refresh so new lines render in the feed below
            setTimeout(() => window.location.reload(), 600);
          }
        } catch (e) {
          toast.error(
            `Brew failed: ${e instanceof Error ? e.message : String(e)}`,
          );
        } finally {
          setBusy(false);
        }
      }}
      className="inline-flex flex-none items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Sparkles className="h-3.5 w-3.5" />
      )}
      Brew Goblin lines
      {pending !== null && (
        <span className="ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
          {pending === -1 ? "!" : pending}
        </span>
      )}
    </button>
  );
}
